import React, { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { GameHeaderBar } from './GameHeaderBar';

const shapes = [
  { name: 'Circle', draw: (color) => <div style={{
    width: 84, height: 84, borderRadius: '50%', background: color
  }} /> },
  { name: 'Square', draw: (color) => <div style={{
    width: 84, height: 84, borderRadius: 14, background: color
  }} /> },
  { name: 'Triangle', draw: (color) => (
    <div style={{
      width: 0, height: 0, borderLeft: '42px solid transparent',
      borderRight: '42px solid transparent', borderBottom: `84px solid ${color}`,
      margin: '0 auto'
    }} />
  ) },
  { name: 'Star', draw: (color) => (
    <svg width="84" height="84" viewBox="0 0 60 60">
      <polygon
        points="30,5 36,22 54,22 39,33 45,50 30,39 15,50 21,33 6,22 24,22"
        fill={color}
      />
    </svg>
  ) }
];

const colors = ['red', 'blue', 'green', 'orange', 'purple'];

export const ColorShapePopGame = ({ handleClose, onGameComplete }) => {
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(1);
  const [choices, setChoices] = useState([]);
  const [target, setTarget] = useState(null);
  const [celebrated, setCelebrated] = useState(false);
  const [shapeStatus, setShapeStatus] = useState({});
  const [animateShape, setAnimateShape] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  const correctSound = useRef(null);
  const errorSound = useRef(null);
  // Set on a right answer until the next round loads - a quick double click
  // used to score twice, and 9 -> 11 skipped the score === 10 finish, so the
  // game could never be completed.
  const roundLockedRef = useRef(false);

  // Load sounds and volume
  useEffect(() => {
    correctSound.current = new Audio('/sounds/correct.mp3');
    errorSound.current = new Audio('/sounds/error.mp3');
    const savedVolume = localStorage.getItem('tapGameVolume');
    if (savedVolume !== null) setVolume(parseFloat(savedVolume));
  }, []);

  // Pick random shapes/colors for choices and target
  const nextRound = () => {
    const shapeChoices = shapes.sort(() => 0.5 - Math.random()).slice(0, Math.min(3 + Math.floor(round/3), shapes.length));
    const colorChoices = colors.sort(() => 0.5 - Math.random()).slice(0, shapeChoices.length);
    const roundChoices = shapeChoices.map((shape, i) => ({
      ...shape,
      color: colorChoices[i]
    }));
    const answer = roundChoices[Math.floor(Math.random() * roundChoices.length)];
    setChoices(roundChoices);
    setTarget(answer);
    setShapeStatus({});
    setAnimateShape('');
    roundLockedRef.current = false;
    setTimeout(() => speakTarget(answer), 400);
  };

  useEffect(() => {
    nextRound();
    // eslint-disable-next-line
  }, [round]);

  // Celebrate and handleClose after 10 rounds
  useEffect(() => {
    if (score >= 10 && !celebrated) {
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      setCelebrated(true);
      setTimeout(() => {
        onGameComplete?.(3, score * 10); // 3 stars, score
        handleClose?.();
      }, 1200);
    }
  }, [score, celebrated, handleClose, onGameComplete]);

  // Speak target
  useEffect(() => {
    if (target && !isMuted) {
      const speakTimeout = setTimeout(() => {
        window.speechSynthesis.cancel();
        const utter = new window.SpeechSynthesisUtterance(`Tap the ${target.color} ${target.name}`);
        utter.rate = 0.9;
        utter.volume = volume;
        window.speechSynthesis.speak(utter);
      }, 600);
      return () => clearTimeout(speakTimeout);
    }
  }, [target, isMuted, volume]);

  const speakTarget = (answer) => {
    if (isMuted || !answer) return;
    window.speechSynthesis.cancel();
    const utter = new window.SpeechSynthesisUtterance(`Tap the ${answer.color} ${answer.name}`);
    utter.rate = 0.9;
    utter.volume = volume;
    window.speechSynthesis.speak(utter);
  };

  const handleClick = (choice) => {
    if (roundLockedRef.current || !target) return;
    const key = choice.name + choice.color;
    if (choice.name === target.name && choice.color === target.color) {
      roundLockedRef.current = true;
      if (!isMuted && correctSound.current) {
        correctSound.current.volume = volume;
        correctSound.current.play();
        const utter = new SpeechSynthesisUtterance('Correct!');
        utter.volume = volume;
        window.speechSynthesis.speak(utter);
      }
      setAnimateShape(key);
      setShapeStatus(prev => ({ ...prev, [key]: 'correct' }));
      setTimeout(() => setAnimateShape(''), 500);
      setScore(prev => prev + 1);
      setTimeout(() => setRound(r => r + 1), 400);
    } else {
      if (!isMuted && errorSound.current) {
        errorSound.current.volume = volume;
        errorSound.current.play();
        const utter = new SpeechSynthesisUtterance('Try again!');
        utter.volume = volume;
        window.speechSynthesis.speak(utter);
      }
      setShapeStatus(prev => ({ ...prev, [key]: 'wrong' }));
      setAnimateShape(key);
      setTimeout(() => setShapeStatus(prev => ({ ...prev, [key]: null })), 700);
      setTimeout(() => setAnimateShape(''), 500);
    }
  };

  return (
    <div className="tap-letter-board-bg">
      <GameHeaderBar
        score={score}
        total={10}
        questionLabel="Tap the:"
        question={target ? <span style={{ color: target.color }}>{target.color} {target.name}</span> : ''}
        volume={volume}
        onVolumeChange={(v) => {
          setVolume(v);
          localStorage.setItem('tapGameVolume', v);
        }}
        onReplay={() => {
          if (target && !isMuted) {
            const utterance = new window.SpeechSynthesisUtterance(`Tap the ${target.color} ${target.name}`);
            utterance.rate = 0.9;
            utterance.volume = volume;
            window.speechSynthesis.speak(utterance);
          }
        }}
      />

      <div className="d-flex flex-wrap justify-content-center" style={{ gap: 10, marginBottom: 10 }}>
        {choices.map(choice => {
          const key = choice.name + choice.color;
          return (
            <button
              key={key}
              className={`btn m-1 letter-btn
                ${animateShape === key ? (shapeStatus[key] === 'wrong' ? 'shake' : 'pulse') : ''}
                ${shapeStatus[key] === 'correct' ? 'btn-success text-white selected-correct' :
                  shapeStatus[key] === 'wrong' ? 'btn-danger text-white shake' :
                  'btn-outline-primary'
                }`}
              onClick={() => handleClick(choice)}
              disabled={score >= 10}
              style={{
                padding: 0,
                width: 130,
                height: 150,
                borderRadius: 18,
                background: score === 10 ? '#e0e0e0' : '#fff',
                boxShadow: score === 10 ? 'none' : '0 2px 8px #0001',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {choice.draw(choice.color)}
              <span style={{ marginTop: 8, fontWeight: 700, fontSize: '1.15rem' }}>{choice.name}</span>
            </button>
          );
        })}
      </div>

      {score === 10 && <h4 className="text-success mt-3">🎉 Well done! All rounds completed.</h4>}

      <style>
        {`
          .tap-letter-board-bg {
            background: url('/image/board.png'), linear-gradient(135deg,rgba(62, 62, 62, 0.3) 0%,rgba(15, 15, 14, 0.41) 100%);
            background-size: cover, cover;
            background-position: center, center;
            border-radius: 32px;
            box-shadow: 0 6px 32px #e0c68a33;
            padding: 32px 12px 24px 12px;
            margin: 0 auto;
            max-width: 900px;
            width: 98%;
            min-height: 400px;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            z-index: 2;
            animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
          }
          @keyframes board-pop {
            0% { transform: scale(0.9); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          .tap-header-bar {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-radius: 20px;
            box-shadow: 0 2px 8px rgba(224,198,138,0.10);
            padding: 1px 18px 18px 18px;
            margin-bottom: 18px;
            gap: 0;
          }
          .tap-score-box,
          .tap-volume-box {
            display: flex;
            align-items: center;
            border-radius: 14px;
            padding: 6px 14px;
            font-weight: 700;
            font-size: 1.1rem;
            color: #b8860b;
            background: rgba(255, 255, 255, 0.86);
            min-width: 110px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
          }
          .tap-score-label {
            margin-right: 6px;
            font-size: 1rem;
            color: #b8860b;
          }
          .tap-score-value {
            font-size: 1.1rem;
            color: #1976d2;
            margin-left: 4px;
            font-weight: 700;
          }
          .tap-letter-target {
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(224,198,138,0.92);
            border-radius: 18px;
            padding: 8px 32px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
            margin: 0 18px;
            min-width: 180px;
          }
          .tap-letter-label {
            font-size: 1.1rem;
            color: #3e2723;
            margin-right: 8px;
            font-weight: 600;
          }
          .tap-letter-value {
            font-size: 2rem;
            color: #1976d2;
            font-weight: 900;
            letter-spacing: 2px;
            text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
          }
          .tap-volume-value {
            margin-left: 8px;
            font-size: 1rem;
            color: #1976d2;
            font-weight: 700;
            min-width: 36px;
            display: inline-block;
            text-align: right;
          }
          .letter-btn {
            padding: 0;
            border-radius: 18px !important;
            background: linear-gradient(135deg,rgb(250, 248, 240) 0%,rgb(248, 228, 166) 100%) !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.10);
            border: 2px solid rgba(223, 214, 192, 0.65) !important;
            min-width: 90px;
            min-height: 60px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.1s, box-shadow 0.2s;
          }
          .letter-btn:active, .letter-btn:focus {
            transform: scale(0.96);
            box-shadow: 0 0 0 3px #ffe08255;
          }
          .pulse {
            animation: pulse 0.5s;
          }
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.12); }
            100% { transform: scale(1); }
          }
          .shake {
            animation: shake 0.4s;
          }
          @keyframes shake {
            0% { transform: translateX(0); }
            20% { transform: translateX(-8px); }
            40% { transform: translateX(8px); }
            60% { transform: translateX(-8px); }
            80% { transform: translateX(8px); }
            100% { transform: translateX(0); }
          }
          .selected-correct,
          .btn-success.selected-correct {
            background: #000000 !important;
            border-color:rgb(0, 245, 253) !important;
            color: #fff !important;
            box-shadow: 0 0 16px 2px rgba(122, 255, 244, 0.6);
            animation: correct-pop 0.5s;
            transparent: none !important;
          }
          @keyframes correct-pop {
            0% { transform: scale(1.15); }
            100% { transform: scale(1); }
          }
          /* Responsive styles */
          @media (max-width: 1024px) {
            .tap-letter-board-bg {
              max-width: 99vw;
              padding: 18px 2vw 18px 2vw;
              min-height: 260px;
            }
            .letter-btn {
              min-width: 70px;
              min-height: 48px;
            }
          }
          @media (max-width: 700px) {
            .tap-header-bar {
              flex-direction: column;
              gap: 10px;
              padding-bottom: 10px;
            }
            .tap-letter-target {
              min-width: 0;
              width: 100%;
              padding: 8px 0;
              margin: 8px 0;
            }
            .tap-letter-board-bg {
              padding: 10px 2vw 10px 2vw;
              min-height: 180px;
            }
            .letter-btn {
              min-width: 48px;
              min-height: 32px;
            }
          }
          @media (max-width: 480px) {
            .tap-letter-board-bg {
              padding: 6px 1vw 6px 1vw;
              min-height: 120px;
            }
            .tap-header-bar {
              padding: 6px 4px 6px 4px;
            }
            .letter-btn {
              min-width: 36px;
              min-height: 24px;
            }
          }
        `}
      </style>
    </div>
  );
};